import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { RefreshCw } from 'lucide-react';

import { useSyncStatus, useTriggerRefresh } from '@/lib/api/adminHooks';
import type { SyncRun } from '@/types/admin';

/**
 * Top-of-page freshness indicator — "Last synced …" per cache kind with a
 * single Refresh button that kicks off an incremental re-pull of every
 * CP → Supabase sync. Polls the status endpoint every 3s while a sync is
 * running so the badge animates out on completion without waiting on
 * react-query's default staleTime.
 */
export function SyncStatusBar() {
  const { data } = useSyncStatus();
  const trigger = useTriggerRefresh();
  const queryClient = useQueryClient();
  const wasRunning = useRef(false);

  useEffect(() => {
    if (wasRunning.current && data && !data.running) {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'overview-snapshot'],
      });
    }
    wasRunning.current = Boolean(data?.running);
  }, [data, queryClient]);

  const latestByKind = summarizeRuns(data?.last_runs ?? []);

  return (
    <div className="flex flex-wrap items-center gap-16 p-12 border border-line rounded-lg bg-raised">
      <div className="flex-1 flex flex-wrap items-center gap-x-20 gap-y-4 type-label-sm text-ink-muted">
        <SyncKindBadge label="Subscriptions" run={latestByKind.subscriptions} />
        <SyncKindBadge label="Orders" run={latestByKind.orders} />
        <SyncKindBadge label="Products" run={latestByKind.products} />
      </div>

      <button
        type="button"
        onClick={() => trigger.mutate()}
        disabled={data?.running || trigger.isPending}
        className="flex items-center gap-6 type-label-sm px-12 py-6 rounded border border-line hover:bg-sunken disabled:opacity-60"
      >
        <RefreshCw
          size={14}
          strokeWidth={1.5}
          className={data?.running ? 'animate-spin' : undefined}
        />
        {data?.running ? 'Refreshing…' : 'Refresh'}
      </button>
    </div>
  );
}

function SyncKindBadge({
  label,
  run,
}: {
  label: string;
  run: SyncRun | null;
}) {
  if (!run) {
    return (
      <span>
        <span className="font-medium text-ink">{label}:</span> never synced
      </span>
    );
  }
  const when = run.finished_at ? new Date(run.finished_at) : null;
  const agoText = when ? `${formatDistanceToNow(when)} ago` : 'in progress';
  const bad = run.status === 'failed';
  return (
    <span className={bad ? 'text-danger' : undefined}>
      <span className="font-medium text-ink">{label}:</span> {agoText}
      {bad && run.error ? ` — ${run.error.slice(0, 120)}` : ''}
    </span>
  );
}

function summarizeRuns(runs: SyncRun[]): Record<string, SyncRun | null> {
  const out: Record<string, SyncRun | null> = {
    subscriptions: null,
    orders: null,
    products: null,
  };
  for (const r of runs) {
    if (r.status !== 'success' && r.status !== 'failed') continue;
    if (!(r.kind in out) || out[r.kind]) continue;
    out[r.kind] = r;
  }
  return out;
}
